import { Extension } from "@tiptap/core"
import { GapCursor } from "@tiptap/pm/gapcursor"
import { NodeSelection, Plugin, PluginKey } from "@tiptap/pm/state"

const tableCellMediaParagraphKey = new PluginKey("tableCellMediaParagraph")

const CELL_TYPES = new Set(["tableCell", "tableHeader"])
const MEDIA_TYPES = new Set(["image", "figure"])

function isEmptyParagraph(node) {
  return node.type.name === "paragraph" && node.content.size === 0
}

function isMediaBlock(node) {
  return MEDIA_TYPES.has(node.type.name)
}

function cellHasMedia(cell) {
  for (let index = 0; index < cell.childCount; index += 1) {
    if (isMediaBlock(cell.child(index))) return true
  }
  return false
}

function selectionTouchesRange(selection, from, to) {
  return selection.from < to && selection.to > from
}

function stripUnselectedEmptyParagraphs(newState) {
  const { selection } = newState
  let tr = newState.tr
  let changed = false

  newState.doc.descendants((node, pos) => {
    if (!CELL_TYPES.has(node.type.name) || !cellHasMedia(node)) return true

    const deletions = []
    let childPos = pos + 1

    for (let index = 0; index < node.childCount; index += 1) {
      const child = node.child(index)
      const childEnd = childPos + child.nodeSize
      const previousChild = index > 0 ? node.child(index - 1) : null

      if (
        isEmptyParagraph(child) &&
        previousChild &&
        isMediaBlock(previousChild) &&
        !selectionTouchesRange(selection, childPos, childEnd)
      ) {
        deletions.push({ from: childPos, to: childEnd })
      }

      childPos = childEnd
    }

    for (let index = deletions.length - 1; index >= 0; index -= 1) {
      const { from, to } = deletions[index]
      const mappedFrom = tr.mapping.map(from)
      const mappedTo = tr.mapping.map(to)
      tr = tr.delete(mappedFrom, mappedTo)
      changed = true
    }

    return true
  })

  return changed ? tr : null
}

function findCellDepth($pos) {
  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    if (CELL_TYPES.has($pos.node(depth).type.name)) return depth
  }
  return -1
}

function mediaBeforeEmptyParagraph($from) {
  if ($from.parent.type.name !== "paragraph" || $from.parent.content.size > 0) {
    return null
  }
  if ($from.parentOffset !== 0) return null

  const $before = $from.doc.resolve($from.before($from.depth))
  const previous = $before.nodeBefore
  if (!previous) return null

  const previousPos = $before.pos - previous.nodeSize
  let mediaPos = null

  if (isMediaBlock(previous)) {
    mediaPos = previousPos
  } else if (
    previous.type.name === "paragraph" &&
    previous.childCount === 1 &&
    isMediaBlock(previous.firstChild)
  ) {
    mediaPos = previousPos + 1
  }

  if (mediaPos == null) return null

  return {
    mediaPos,
    from: $from.before($from.depth),
    to: $from.after($from.depth),
    cellDepth: findCellDepth($from),
  }
}

function gapCursorAfterMedia(doc, mediaPos) {
  const media = doc.nodeAt(mediaPos)
  if (!media || !isMediaBlock(media)) return null

  const afterMedia = mediaPos + media.nodeSize
  return new GapCursor(doc.resolve(afterMedia))
}

function selectionCellPos(selection) {
  const cellDepth = findCellDepth(selection.$from)
  return cellDepth === -1 ? null : selection.$from.before(cellDepth)
}

function confineSelectionToSameCell(oldState, newState) {
  const info = mediaBeforeEmptyParagraph(oldState.selection.$from)
  if (!info || info.cellDepth === -1) return null

  const cellPos = oldState.selection.$from.before(info.cellDepth)
  if (selectionCellPos(newState.selection) === cellPos) return null

  const nextSelection = gapCursorAfterMedia(newState.doc, info.mediaPos)
  return nextSelection ? newState.tr.setSelection(nextSelection) : null
}

function deleteEmptyParagraphAfterMedia({ editor }) {
  const { state, view } = editor
  if (!state.selection.empty) return false

  const info = mediaBeforeEmptyParagraph(state.selection.$from)
  if (!info) return false

  const tr = state.tr.delete(info.from, info.to)
  const media = tr.doc.nodeAt(info.mediaPos)
  if (!media || !isMediaBlock(media)) return false

  if (info.cellDepth === -1) {
    // Outside a table: land on the image. Default Backspace would delete it.
    tr.setSelection(NodeSelection.create(tr.doc, info.mediaPos))
  } else {
    // Inside a table: do not NodeSelect the image (Backspace would delete it)
    // and do not let the caret remap into the next cell.
    const nextSelection = gapCursorAfterMedia(tr.doc, info.mediaPos)
    if (nextSelection) tr.setSelection(nextSelection)
  }

  view.dispatch(tr)
  return true
}

export const TableCellMediaParagraph = Extension.create({
  name: "tableCellMediaParagraph",
  priority: 1000,

  addKeyboardShortcuts() {
    return {
      Backspace: deleteEmptyParagraphAfterMedia,
      Delete: deleteEmptyParagraphAfterMedia,
    }
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: tableCellMediaParagraphKey,
        appendTransaction: (transactions, oldState, newState) => {
          if (!transactions.some((transaction) => transaction.docChanged)) return null
          return (
            confineSelectionToSameCell(oldState, newState) ||
            stripUnselectedEmptyParagraphs(newState)
          )
        },
      }),
    ]
  },
})

export default TableCellMediaParagraph
